<!--
  Creates comparable field to help <comparator> comparing data.
  2 items are considered as the "same" if and only if
    their comparable fields are deep equal

  Properties:
  - field: the name of the field, to differenciate different comparable.
      if not defined but prop is a string, prop is used
      if still not defined, "" is used
  - prop: the property to compare the value of
    - true or "": the full value is kept for comparison
    - "field_name": value.field_name is used for comparison
    - (value, compare) => {...}: the function is called for comparison
  - default: the value to use instead of undefined
  - expected: used for radio buttons
      the value that should trigger a status change
  - diff: used for checkboxes
      if true, this property won't be used in <comparator>,
      but only to trigger the "diff" status
  - ignore: when true, the comparable is ignored
  - listeners: a dict of listeners, that will be forwarded to the template
      only when displaying the view (and not the compare)
      to be used in v-on
  - attrs: a dict of attributes, that will be forwarded to the template
      only when displaying the view (and not the compare)
      to be used in v-bind
  - :prop="...": merged with attrs
  - @event="...": merge with listeners

  The template recieves:
  - value: the main property value
  - other: the property value compared with
  - status: status of the comparable
    - "same": view and comapre values are deep equal
    - "diff": with "diff" attribute only,
        view and comapre value are different
    - "new": view and comapre values are different,
        in this template is the view value
    - "old": view and comapre values are different,
        in this template is the compare value
    - "other": with "expected" attribute ony,
        neither view not comapre values are expected
    - "ignore": with "ignore" attribute, comparable is ignored
  - view: if displaying the view
  - compare: if displaying the compare (=!view)
  - prop: the prop passed to <comparable>,
      only when displaying the view (and not the compare)
  - listeners: the listeners passed to <comparable>,
      only when displaying the view (and not the compare)
      to be used in v-on
  - attrs: the attributes passed to <comparable>,
      only when displaying the view (and not the compare)
      to be used in v-bind

  Usage:
  <comparable
    :prop="field"
    @focus="handleFocus">
    <template v-slot="{ value, compare, status, listeners }">
      <input type="text"
        :class="'compare-' + status"
        v-model="value.field"
        :disabled="compare"
        v-on="listeners"/>
    </template>
  </comparable>
-->

<script>
import deeEqual from 'fast-deep-equal'

export default {
  name: 'Comparable',
  props: {
    field: String,
    prop: [String, Function, Boolean],
    default: {},
    expected: {},
    listeners: Object,
    attrs: Object,
    diff: Boolean,
    ignore: Boolean,
  },
  inject: {
    // look for the ComparatorVue
    comparator: { default: null },
  },
  computed: {
    /* merges the listeners */
    computedListeners () {
      return {
        ...this.$listeners,
        ...(this.listeners || {}),
      }
    },
    /* merges the attrs */
    computedAttrs () {
      return {
        ...this.$attrs,
        ...(this.attrs || {}),
      }
    },
    /* the prop attribute as a (value, compare) => {...} function */
    computedProp () {
      // true or "", return the value unchanged
      if (this.prop === true || this.prop === '') return obj => obj
      // a string, split by "." and get it
      else if (this.prop && typeof this.prop === 'string') {
        let path = this.prop.replace(/\[(\w+)\]/g, '.$1')
        path = path.replace(/^\./, '')
        let keys = path.split('.')
        return obj => {
          for (let key of keys) {
            if (!obj) return undefined
            else if (key in obj) obj = obj[key]
            else return undefined
          }
          return obj
        }
      // should be a function
      } else return this.prop || (() => null)
    },
    /* the field attribute, or the prop attribute if it is a string */
    computedField () {
      if (this.field !== undefined) return this.field
      else if (typeof this.prop === 'string') return this.prop
      else return ''
    },
    /* the view property value */
    value () {
      if (!this.comparator) return undefined
      // get the property value
      let value = this.computedProp(this.comparator.value, !this.compare)
      // return default if undefined
      return value === undefined ? this.default : value
    },
    /* the compare property value */
    other () {
      if (!this.comparator) return undefined
      // check that there is a "other"
      let other = this.comparator.other
      if (other === undefined) return other
      // get the property value
      let value = this.computedProp(other, false)
      // return default if undefined
      return value === undefined ? this.default : value
    },
    /* the comparison */
    status () {
      // ignored, return "ignore"
      if (this.ignore) return 'ignore'
      // no compare, return "same"
      if (!this.comparator || this.comparator.compare === undefined) {
        return 'same'
      }
      // there is an "other"
      if (this.comparator.other !== undefined) {
        // they're deep equal, return "same"
        if (deeEqual(this.value, this.other)) return 'same'
        // compare with the expected value
        else if (this.expected !== undefined) {
          // value is expected
          if (deeEqual(this.value, this.expected)) {
            return this.comparator.compare ? 'old' : 'new'
          // other value is expected
          } else if (deeEqual(this.other, this.expected)) {
            return this.comparator.compare ? 'new' : 'old'
          // none is expected
          } else return 'other'
        } else if (this.diff && !this.comparator.compare) return 'diff'
      // no "other", compare with the expected value
      } else if (this.expected !== undefined) {
        // value is expected
        if (deeEqual(this.value, this.expected)) {
          return this.comparator.compare ? 'old' : 'new'
        // value is not expected
        } else return 'other'
      }
      // normal case
      return this.comparator.compare ? 'old' : 'new'
    },
    /* the compare property value */
    compare () {
      /* true if displaying "other", not "view" */
      if (!this.comparator) return false
      return this.comparator.compare
    },
  },
  /* Just render the template with parameters */
  render () {
    return this.$scopedSlots.default({
      value: this.value,
      other: this.other,
      status: this.status,
      view: !this.compare || undefined,
      compare: this.compare || undefined,
      prop: this.compare ? undefined : this.prop,
      listeners: this.compare ? {} : this.computedListeners,
      attrs: this.compare ? {} : this.computedAttrs,
    })
  },
  /* init comparable */
  created () { this.setComparable(this.value) },
  /* clear comparable */
  beforeDestroy () { this.setComparable(undefined) },
  watch: {
    /* update comparable */
    value: {
      handler (value) { this.setComparable(value) },
      deep: true,
    },
  },
  methods: {
    /* notifies the comparator that the comparable changed */
    setComparable (value) {
      if (!this.comparator || this.diff || this.ignore) return
      this.comparator.setComparable(this.computedField, value)
    },
  },
}
</script>
